'use client';

import { forwardRef, useContext, useMemo } from 'react';

import { Text } from '../text/text';
import { GleamRootContext } from './context/gleam-root.context';
import { gleamLabelRecipe } from './styles/gleam-label.styles';
import type { GleamLabelPropsInterface } from './types/gleam-label.types';

/**
 * @since 10.0.0
 */

export const GleamLabel = forwardRef<HTMLSpanElement, GleamLabelPropsInterface>(
  (props: GleamLabelPropsInterface, forwardedRef) => {
    const {
      appearance = 'ellipsis',
      tone = 'default',
      onImpact = false,
      count,
      maxCount = 99,
      maxSuffix = false,
      css: cssProp,
      renderDot,
      ...rest
    } = props;

    const { hasMultipleChildren, floatingOffset } = useContext(GleamRootContext);

    const value = useMemo(() => {
      if (count <= maxCount) {
        return count;
      }
      return maxSuffix ? `${maxCount}+` : maxCount;
    }, [count, maxSuffix, maxCount]);

    return (
      <Text
        ref={forwardedRef}
        data-c11n-component="Gleam.Label"
        data-c11n-version={__C11N_VERSION__}
        css={{
          ...gleamLabelRecipe.raw({
            appearance,
            floating: hasMultipleChildren,
            floatingComponent: floatingOffset?.component,
            floatingOffset: floatingOffset?.size,
            onImpact,
            tone,
          }),
          ...cssProp,
        }}
        {...rest}
      >
        {appearance === 'dot' && renderDot ? renderDot({ tone, onImpact, count }) : value}
      </Text>
    );
  },
);

GleamLabel.displayName = 'Gleam.Label';
